import React, { useMemo, useState } from 'react';
import moment from 'moment-timezone';

import AddressLookup from 'views/site/AddressLookup.js';
import Appointment from 'files/Appointment.js';
import AppointmentPicker from 'views/site/AppointmentPicker.js';
import Button from 'views/site/Button.js';
import CalendlyScheduler from 'views/site/CalendlyScheduler.js';
import Icon from 'views/site/Icon.js';
import Section from 'views/site/Section.js';
import Theme from 'styles/Theme.js';
import Utils from 'files/Utils.js';

/*
 * Contact
 *
 * A real page at ?route=contact, rather than a scroll to a section buried at
 * the bottom of the home page. "Contact" in the navigation of a local business
 * is a destination people expect to be able to link to, and the previous
 * behaviour — jump to home, scroll to an anchor — silently did nothing at all
 * once that anchor stopped being rendered.
 *
 * Two reasons bring somebody here, and the form asks which before it asks
 * anything else: they want somebody to come out, or they have a question.
 * Booking is offered first and chosen for them, because it is the one a
 * visitor should not have to go through the blind spot checker to find. It is
 * the same booking the checker ends in — details, then a time or the dealer's
 * Calendly, then one request — and it goes out through files/Appointment.js
 * exactly as the checker's does.
 *
 * The form is our own rather than the platform's embedded support form. An
 * iframe cannot take the page's typography, cannot be validated before it is
 * submitted, cannot report an error in the page's own voice, and reports its
 * height by postMessage which never quite settles.
 */

const FIELDS = ['firstName', 'lastName', 'email', 'phone', 'address', 'message'];

// Browser autofill reads these rather than guessing from the label, which is
// how the phone number was being skipped.
const AUTOCOMPLETE = {
    email: 'email',
    firstName: 'given-name',
    lastName: 'family-name',
    phone: 'tel'
};

const Contact = ({ dealership, utils }) => {

    const mobile = Utils.isMobile();

    const [attempted, setAttempted] = useState(false);
    const [confirmation, setConfirmation] = useState(null);
    const [customer, setCustomer] = useState({ address: null, email: '', firstName: '', lastName: '', message: '', phone: '' });
    const [error, setError] = useState(null);
    const [intent, setIntent] = useState(null);
    const [selected, setSelected] = useState(null);
    const [step, setStep] = useState('intent');
    const [submitting, setSubmitting] = useState(false);

    const booking = intent === 'appointment';
    const calendly = useMemo(() => Appointment.getCalendly(dealership), [dealership]);
    const days = calendly ? [] : Appointment.getOpenDays(dealership);
    const canSchedule = calendly !== null || days.length > 0;

    const rules = booking
        ? { requireAddress: true, requireMessage: false, requirePhone: true }
        : { requireAddress: false, requireMessage: true, requirePhone: false };

    // nothing is flagged until they try to send, and a flag clears the moment
    // the field is right
    const problems = attempted ? Appointment.getCustomerProblems(customer, rules) : {};

    if(!dealership) {
        return null;
    }

    const setValue = (key, value) => setCustomer(current => ({ ...current, [key]: value }));

    const onChoose = next => {
        setAttempted(false);
        setError(null);
        setIntent(next);

        // Calendly asks for the name, email, phone and address itself, so a
        // dealer who uses it gets the calendar straight away
        setStep(next === 'appointment' && calendly ? 'schedule' : 'details');
    }

    const onSubmit = async appointment => {
        setError(null);
        setSubmitting(appointment ? 'appointment' : 'callback');
        try {
            await Appointment.submit(utils, {
                appointment: appointment,
                customer: booking ? customer : { ...customer, address: null },
                dealership: dealership,
                form: 'contact',
                message: customer.message
            });
            setConfirmation(!booking ? { kind: 'message' } : appointment ? { ...appointment, kind: 'requested' } : { kind: 'callback' });
        } catch(e) {
            setError(e.message || 'Something went wrong. Please try again.');
        } finally {
            setSubmitting(false);
        }
    }

    const onContinue = () => {
        setError(null);
        setAttempted(true);
        let found = Appointment.getCustomerProblems(customer, rules);
        let first = FIELDS.find(key => found[key]);
        if(first) {
            document.getElementById(`contact-field-${first}`)?.focus();
            return;
        }
        if(booking && canSchedule) {
            setSelected(null);
            setStep('schedule');
            return;
        }
        onSubmit(null);
    }

    // what they typed survives going back, including back to the choice - a
    // visitor who picked the wrong path should not have to type it all again
    const onBack = () => {
        setError(null);
        setStep(step === 'schedule' && !calendly ? 'details' : 'intent');
    }

    const getFieldStyle = key => ({
        ...Theme.styles.input(),
        border: `1px solid ${problems[key] ? Theme.colors.error : Theme.colors.border()}`,
        borderRadius: 12
    });

    const getHint = key => problems[key] && problems[key] !== 'missing' ? (
        <span style={{
            ...Theme.textStyles.caption({ color: Theme.colors.error }),
            display: 'block',
            marginTop: 6
        }}>{problems[key]}</span>
    ) : null;

    const getLabel = (text, optional, htmlFor) => (
        <label
        htmlFor={htmlFor}
        style={{
            ...Theme.textStyles.body({ color: Theme.colors.text(), fontSize: 13, fontWeight: 600 }),
            display: 'block',
            marginBottom: 6
        }}>
            {text}
            {optional && (
                <span style={{ color: Theme.colors.lightGrey, fontWeight: 400 }}>{' (optional)'}</span>
            )}
        </label>
    );

    const getInput = ({ key, label, optional, placeholder, type = 'text' }) => (
        <div>
            {getLabel(label, optional, `contact-field-${key}`)}
            <input
            aria-invalid={problems[key] ? true : undefined}
            autoComplete={AUTOCOMPLETE[key]}
            id={`contact-field-${key}`}
            inputMode={type === 'tel' ? 'tel' : undefined}
            name={AUTOCOMPLETE[key]}
            onChange={evt => setValue(key, evt.target.value)}
            placeholder={placeholder}
            style={getFieldStyle(key)}
            type={type}
            value={customer[key]} />
            {getHint(key)}
        </div>
    );

    const getError = () => error && (
        <span style={{
            ...Theme.textStyles.caption({ color: Theme.colors.error }),
            display: 'block'
        }}>{error}</span>
    );

    /*
     * Step one is two answers and nothing else on screen. Shown above the
     * form, the choice read as one more field to fill in; on its own, each
     * card is plainly a way in, and what follows it is plainly the next step.
     * The page heading carries the introduction, so the cards do not repeat
     * it. Booking is first because it is the one worth finding.
     */
    const getIntent = () => (
        <div style={{ display: 'flex', flexDirection: 'column' }}>
            <div style={{
                display: 'grid',
                gap: 16,
                gridTemplateColumns: mobile ? '1fr' : '1fr 1fr'
            }}>
                {[{
                    action: 'Get started',
                    body: "Pick a day and time you'd prefer, and we'll call you to confirm the details before anyone comes out. It's free, and you don't have to buy anything.",
                    icon: 'calendar-event',
                    key: 'appointment',
                    title: 'Book a free evaluation'
                },{
                    action: 'Send a question',
                    body: 'Ask about the network, a network you already have, or anything else. We usually get back to people within one business day.',
                    icon: 'envelope-fill',
                    key: 'question',
                    title: 'Ask a question'
                }].map(option => (
                    <button
                    className={'contact-choice'}
                    key={option.key}
                    onClick={() => onChoose(option.key)}
                    type={'button'}
                    style={{
                        '--contact-choice-accent': Theme.colors.primary(),
                        alignItems: 'flex-start',
                        backgroundColor: Theme.colors.white,
                        border: `1.5px solid ${Theme.colors.border()}`,
                        borderRadius: 20,
                        cursor: 'pointer',
                        display: 'flex',
                        flexDirection: 'column',
                        fontFamily: 'inherit',
                        gap: 14,
                        padding: mobile ? '22px 22px' : '28px 28px',
                        textAlign: 'left'
                    }}>
                        <span style={{
                            alignItems: 'center',
                            backgroundColor: Theme.tint(0.08),
                            borderRadius: 14,
                            display: 'flex',
                            height: 48,
                            justifyContent: 'center',
                            width: 48
                        }}>
                            <Icon color={Theme.colors.primary()} name={option.icon} size={20} />
                        </span>
                        <span style={Theme.textStyles.subHeading({ fontSize: mobile ? 18 : 20 })}>{option.title}</span>
                        <span style={{ ...Theme.textStyles.body({ color: Theme.colors.subText(), fontSize: 15, marginBottom: 0 }), flexGrow: 1 }}>
                            {option.body}
                        </span>
                        <span style={{
                            alignItems: 'center',
                            color: Theme.colors.primary(),
                            display: 'flex',
                            fontSize: 15,
                            fontWeight: 600,
                            gap: 8,
                            marginTop: 4
                        }}>
                            {option.action}
                            <span className={'contact-choice-arrow'} style={{ display: 'flex' }}>
                                <Icon color={Theme.colors.primary()} name={'arrow-right'} size={14} />
                            </span>
                        </span>
                    </button>
                ))}
            </div>
        </div>
    );

    /*
     * Chrome, not a heading. The page heading above names the path and the
     * step; this row is only the way back and how far along they are.
     */
    const getStepBar = () => {
        let total = booking && canSchedule && !calendly ? 3 : 2;
        let current = step === 'schedule' && !calendly ? 3 : 2;
        return (
            <div style={{
                alignItems: 'center',
                display: 'flex',
                gap: 16,
                justifyContent: 'space-between',
                marginBottom: 8
            }}>
                <button
                className={'pressable'}
                onClick={onBack}
                type={'button'}
                style={{
                    alignItems: 'center',
                    background: 'none',
                    border: 'none',
                    color: Theme.colors.subText(),
                    cursor: 'pointer',
                    display: 'inline-flex',
                    fontFamily: 'inherit',
                    fontSize: 14,
                    fontWeight: 500,
                    gap: 6,
                    padding: 0
                }}>
                    <Icon color={Theme.colors.subText()} name={'arrow-right'} size={12} style={{ transform: 'rotate(180deg)' }} />
                    {'Back'}
                </button>
                <div
                aria-label={`Step ${current} of ${total}`}
                role={'img'}
                style={{ alignItems: 'center', display: 'flex', gap: 10 }}>
                    <span style={Theme.textStyles.caption({ color: Theme.colors.subText() })}>
                        {`Step ${current} of ${total}`}
                    </span>
                    <span style={{ display: 'flex', gap: 4 }}>
                        {Array.from({ length: total }).map((_, index) => (
                            <span
                            key={index}
                            style={{
                                backgroundColor: index < current ? Theme.colors.primaryFill() : Theme.colors.lighterGrey,
                                borderRadius: 2,
                                height: 3,
                                transition: 'background-color 0.3s ease',
                                width: 24
                            }} />
                        ))}
                    </span>
                </div>
            </div>
        )
    };

    /*
     * The page has one heading, and it follows the visitor: it asks the
     * question on the first step and names the path they chose after that.
     * A second title above the form read as a page inside the page.
     */
    const getHero = () => {
        if(step === 'intent' || confirmation) {
            return {
                subtitle: dealership.contact_form_text || "You can request a free in-home evaluation, or send us a question if that's all you need right now. For an evaluation, we'll call you to confirm a time before someone comes out to look at what your current alarms do and don't cover. We usually get back to people within one business day.",
                title: 'Get in touch'
            };
        }
        if(booking === false) {
            return {
                subtitle: "Send us your question and we'll get back to you, usually within one business day. It can be about the network, a network you already have, or anything else. The more detail you include, the easier it is for us to give you a useful answer.",
                title: 'Ask a question'
            };
        }
        if(step === 'schedule') {
            return {
                subtitle: calendly
                    ? "Pick the day and time that works best for you, then fill in your contact details and address. You'll get an email once it's booked, and we'll still call you to confirm everything before anyone comes out to your home."
                    : "Pick the day and time you'd prefer. This isn't a confirmed appointment yet. Once we have your request, we'll call you to confirm the date, time, and address before anyone comes out to your home.",
                title: 'Book a free evaluation'
            };
        }
        return {
            subtitle: canSchedule
                ? "We need a few details first so we know who to call and where your home is. After that, you'll pick a day and time you'd prefer, and we'll call you to confirm it. The evaluation is free, and you're not under any obligation to buy anything."
                : "We need a few details so we know who to call and where your home is. Once we have them, someone from our office will call you to set up a time. The evaluation is free, and you're not under any obligation to buy anything.",
            title: 'Book a free evaluation'
        };
    };

    const getForm = () => (
        <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 16
        }}>
            {getStepBar()}

            <div style={{
                display: 'grid',
                gap: 16,
                gridTemplateColumns: mobile ? '1fr' : '1fr 1fr'
            }}>
                {getInput({ key: 'firstName', label: 'First name', placeholder: 'Jane' })}
                {getInput({ key: 'lastName', label: 'Last name', placeholder: 'Doe' })}
            </div>

            <div style={{
                display: 'grid',
                gap: 16,
                gridTemplateColumns: mobile ? '1fr' : '1fr 1fr'
            }}>
                {getInput({ key: 'email', label: 'Email address', placeholder: 'jane@example.com', type: 'email' })}
                {getInput({ key: 'phone', label: 'Phone number', optional: !booking, placeholder: '(555) 123-4567', type: 'tel' })}
            </div>

            {booking && (
                <div>
                    {getLabel('Home address', false, 'contact-field-address')}
                    <AddressLookup
                    hint={problems.address && problems.address !== 'missing' ? problems.address : null}
                    id={'contact-field-address'}
                    inputStyle={getFieldStyle('address')}
                    invalid={problems.address ? true : false}
                    onChange={address => setValue('address', address)}
                    utils={utils}
                    value={customer.address} />
                </div>
            )}

            <div>
                {getLabel(booking ? 'Is there anything we should know before we visit?' : 'What would you like to ask?', booking, 'contact-field-message')}
                <textarea
                aria-invalid={problems.message ? true : undefined}
                id={'contact-field-message'}
                onChange={evt => setValue('message', evt.target.value)}
                placeholder={booking
                    ? "For example, a gate code, a better time to call, or something about the house we'd want to know ahead of time."
                    : "Tell us what you'd like to know. If it's about your home or a network you already have, a little detail helps us answer it properly."}
                rows={booking ? 3 : 5}
                style={{
                    ...getFieldStyle('message'),
                    borderRadius: 16,
                    lineHeight: 1.6,
                    minHeight: booking ? 96 : 140,
                    resize: 'vertical'
                }}
                value={customer.message} />
            </div>

            {getError()}

            <div style={{ marginTop: 4 }}>
                <Button
                icon={booking ? (canSchedule ? 'calendar-event' : 'send-fill') : 'send-fill'}
                label={booking ? (canSchedule ? 'Continue to pick a time' : 'Request my free evaluation') : 'Send my question'}
                loading={submitting}
                onClick={onContinue}
                style={{ fontSize: 16, padding: '15px 40px', width: mobile ? '100%' : 'auto' }}
                type={'primary'} />
            </div>
        </div>
    );

    const getSchedule = () => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {getStepBar()}

            {calendly ? (
                <CalendlyScheduler
                config={calendly}
                form={'contact'}
                onScheduled={() => setConfirmation({ kind: 'calendly' })} />
            ) : (
                <>
                    <AppointmentPicker
                    days={days}
                    onSelect={setSelected}
                    selected={selected}
                    timezone={Appointment.getTimezone(dealership)} />
                </>
            )}

            {getError()}

            {/* Asking for a call is a real choice, so it is a real button next
                to the one we would rather they press, styled as the quieter of
                the two. Each shows its own spinner so it is clear which one
                was pressed. */}
            {!calendly && (
                <div style={{
                    display: 'flex',
                    flexDirection: mobile ? 'column' : 'row',
                    flexWrap: 'wrap',
                    gap: 12,
                    marginTop: 4
                }}>
                    <Button
                    disabled={submitting === 'appointment'}
                    icon={'telephone-fill'}
                    label={'Call me to find a time'}
                    loading={submitting === 'callback'}
                    onClick={() => onSubmit(null)}
                    style={{
                        border: `1.5px solid ${Theme.colors.border()}`,
                        color: Theme.colors.text(),
                        fontSize: 16,
                        padding: '15px 32px',
                        width: mobile ? '100%' : 'auto'
                    }}
                    type={'outline'} />
                    <Button
                    disabled={!selected?.time || submitting === 'callback'}
                    icon={'send-fill'}
                    label={'Request this appointment'}
                    loading={submitting === 'appointment'}
                    onClick={() => onSubmit(selected)}
                    style={{ fontSize: 16, padding: '15px 40px', width: mobile ? '100%' : 'auto' }}
                    type={'primary'} />
                </div>
            )}
        </div>
    );

    const getSuccess = () => {
        let name = customer.firstName.trim();
        let content = {
            callback: {
                body: `Thanks, ${name}. Someone from our office will give you a call soon to find a time that works for you.`,
                title: "Request Submitted"
            },
            calendly: {
                // Calendly collected the name, so we don't have it here
                body: "Thanks for booking. You'll get an email shortly with the details, and someone from our office will call you to confirm the date, time, and address before anyone comes out.",
                title: "Booking Request Submitted"
            },
            message: {
                body: `Thanks, ${name}. We've received your request and we'll get back to you, usually within one business day.`,
                title: "Request Submitted"
            },
            requested: confirmation?.kind === 'requested' && {
                body: `Thanks, ${name}. You asked for ${moment(`${confirmation.date} ${confirmation.time}`, 'YYYY-MM-DD HH:mm:ss').format('dddd, MMMM D [at] h:mm A')}. Someone from our office will call you to confirm the date, time, and address before anyone comes out to your home.`,
                title: "Request Submitted"
            }
        }[confirmation.kind];
        return (
            <div style={{
                backgroundColor: Theme.colors.offWhite,
                borderRadius: 20,
                padding: mobile ? '48px 24px' : '56px 40px',
                textAlign: 'center'
            }}>
                <div style={{
                    alignItems: 'center',
                    backgroundColor: 'rgba(47,168,79,0.15)',
                    borderRadius: '50%',
                    display: 'flex',
                    height: 56,
                    justifyContent: 'center',
                    margin: '0 auto 18px',
                    width: 56
                }}>
                    <Icon name={'check-lg'} size={24} color={Theme.colors.success} />
                </div>
                <span style={{
                    ...Theme.textStyles.heading({ fontSize: 24 }),
                    display: 'block',
                    marginBottom: 10
                }}>{content.title}</span>
                <span style={{
                    ...Theme.textStyles.body(),
                    display: 'block'
                }}>{content.body}</span>
            </div>
        )
    };

    return (
        <div
        className={'site'}
        style={{
            paddingTop: Theme.layout.headerOffset(),
            width: '100%'
        }}>
            <section style={{
                backgroundColor: Theme.colors.offWhite,
                borderBottom: `1px solid ${Theme.colors.border()}`,
                width: '100%'
            }}>
                <div style={{
                    margin: '0 auto',
                    maxWidth: Theme.layout.maxWidth,
                    padding: mobile ? '40px 20px' : '64px 40px',
                    textAlign: 'center'
                }}>
                    <span style={{
                        ...Theme.textStyles.label(),
                        display: 'block',
                        marginBottom: 14
                    }}>{'Contact'}</span>
                    <span style={{
                        ...Theme.textStyles.sectionHeading(),
                        display: 'block'
                    }}>{getHero().title}</span>
                    <span style={{
                        ...Theme.textStyles.sectionSubheading(),
                        display: 'block',
                        margin: '16px auto 0'
                    }}>{getHero().subtitle}</span>
                </div>
            </section>

            <Section>
                <div
                id={'contact-form'}
                style={{
                    margin: '0 auto',
                    maxWidth: 640
                }}>
                    <div className={'contact-step'} key={confirmation ? 'done' : step}>
                        {confirmation
                            ? getSuccess()
                            : { details: getForm, intent: getIntent, schedule: getSchedule }[step]()}
                    </div>
                </div>
            </Section>
        </div>
    )
}

export default Contact;
